/**
 * The playback clock, in sim seconds. Real time drives it at a speed (1x, 2x); a hit-stop freezes
 * it for a few real milliseconds; a hold stops it at a sim time until released (a line the player
 * reads before the play goes on); a skip jumps to the whistle. It never runs past the whistle, so
 * no sprite can be drawn somewhere the log has not put it yet.
 */
export class Clock {
  /** Sim seconds. Negative during the pre-snap hold, when everyone stands in the formation. */
  t: number;
  speed = 1;
  private frozenMs = 0;
  /** Stop here instead of at the whistle (the `?t=` screenshot parameter). */
  private readonly stopAt: number;
  /** Sim times the clock stops at until released, one entry per hold (a time may repeat). */
  private holds: number[] = [];

  constructor(
    readonly end: number,
    leadInSeconds: number,
    stopAt: number | null = null,
  ) {
    this.t = -leadInSeconds;
    this.stopAt = stopAt === null ? end : Math.min(end, stopAt);
  }

  /** Advance by a frame of real time; returns the new sim time. */
  tick(realMs: number): number {
    let ms = realMs;
    if (this.frozenMs > 0) {
      const used = Math.min(this.frozenMs, ms);
      this.frozenMs -= used;
      ms -= used;
    }
    this.t = Math.min(this.stopAt, this.nextHold, this.t + (ms / 1000) * this.speed);
    return this.t;
  }

  /** Stop at each of these sim times until it is released. */
  holdAt(times: readonly number[]): void {
    this.holds = [...this.holds, ...times].sort((a, b) => a - b);
  }

  /** Release one hold at sim time `t`; the clock runs on once none is left there. */
  release(t: number): void {
    const i = this.holds.indexOf(t);
    if (i >= 0) this.holds.splice(i, 1);
  }

  clearHolds(): void {
    this.holds = [];
  }

  /** Held: stopped at a hold that is not released yet. */
  get held(): boolean {
    return this.holds.length > 0 && this.t >= (this.holds[0] as number);
  }

  private get nextHold(): number {
    return this.holds[0] ?? Infinity;
  }

  /** Freeze for this many real milliseconds (0 under reduced motion). */
  hitStop(ms: number): void {
    this.frozenMs = Math.max(this.frozenMs, ms);
  }

  skip(): void {
    this.frozenMs = 0;
    this.holds = [];
    this.t = this.stopAt;
  }

  get done(): boolean {
    return this.t >= this.stopAt;
  }
}
